import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getStoredLocale, setStoredLocale } from './utils';
import {
  type I18nContextType,
  type I18nInstance,
  type InterpolationVars,
  type Locale,
  type TArgs,
  type TranslationKeys,
  type TranslationKeyValues,
} from './types';

const I18nContext = createContext<I18nContextType | null>(null);

interface I18nProviderProps {
  children: React.ReactNode;
  instance: I18nInstance;
  /** Optional: Explicitly set the initial locale, overriding storage/default */
  initialLocale?: Locale;
}

export const I18nProvider = ({
  children,
  instance,
  initialLocale: explicitInitialLocale,
}: I18nProviderProps) => {
  const [locale, setCurrentLocale] = useState<Locale>(() => {
    // Determine initial locale on the client
    if (
      explicitInitialLocale &&
      instance.config.resources[explicitInitialLocale]
    ) {
      return explicitInitialLocale;
    }
    const stored = getStoredLocale(instance.config.storageKey);
    if (stored && instance.config.resources[stored]) {
      return stored;
    }
    return instance.config.defaultLocale;
  });

  // Update storage whenever locale changes
  useEffect(() => {
    setStoredLocale(instance.config.storageKey, locale);
  }, [locale, instance.config.storageKey]);

  // Function to change the locale
  const setLocale = useCallback(
    (newLocale: Locale) => {
      if (instance.config.resources[newLocale]) {
        setCurrentLocale(newLocale);
      } else {
        console.warn(
          `[i18n] Attempted to set unsupported locale: "${newLocale}". Available: ${instance.getAvailableLocales().join(', ')}`
        );
      }
    },
    [instance]
  ); // Include instance in dependencies

  // Memoized translation function bound to the current locale
  const t = useCallback(
    <
      K extends keyof TranslationKeys,
      Values extends TranslationKeyValues | void = InterpolationVars<K>,
    >(
      key: K,
      ...args: TArgs<K, Values> // Accept typed args
    ): string => {
      // The instance.t needs the key, potentially values, and the CURRENT locale
      const values = args[0]; // Extract potential values
      const tArgs = values ? ([values, locale] as const) : ([locale] as const);

      return instance.t(key, ...(tArgs as any));
    },
    [instance, locale]
  ); // Recreate if instance or locale changes

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo<I18nContextType>(
    () => ({
      locale,
      setLocale,
      t,
      i18nInstance: instance,
    }),
    [locale, setLocale, t, instance]
  );

  return (
    <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>
  );
};

export const useTranslation = (): I18nContextType => {
  const context = useContext(I18nContext);

  if (context === null) {
    throw new Error(
      '[i18n] `useTranslation` must be used within an <I18nProvider>'
    );
    // Alternatively, you could provide a non-functional fallback here,
    // but throwing makes the setup requirement clear.
  }

  return context;
};
